/**
 * Navigation.js
 *
 * Reusable navigation bar component used across all pages.
 * Includes authentication support, notifications, and user menu.
 */

import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SERVER_URL } from '../config';
import { NOTIFICATION_POLL_INTERVAL } from '../utils/constants';
import { useAuth } from '../AuthContext';
import Avatar from './Avatar';
function Navigation() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationsRef = useRef(null);
  const [pendingPools, setPendingPools] = useState([]);
  const [acceptedPools, setAcceptedPools] = useState([]);
  const [recentFollowers, setRecentFollowers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  // Load notifications and user data if authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // Fetch pending pools
      fetch(`${SERVER_URL}/users/${encodeURIComponent(user.email)}/pending-pools`)
        .then(res => res.json())
        .then(data => {
          setPendingPools(data.pending_pools);
        })
        .catch(err => console.error('Error loading pending pools:', err));

      // Fetch user's profile to get accepted pools
      fetch(`${SERVER_URL}/users/${user.id}`)
        .then(res => res.json())
        .then(data => {
          const accepted = (data.posts || []).filter(
            post =>
              post.beneficiary_email === user.email &&
              post.acceptance_status === 'accepted'
          );
          setAcceptedPools(accepted);
        })
        .catch(err => console.error('Error loading accepted pools:', err));

      // Fetch followers
      fetch(`${SERVER_URL}/users/${user.id}/followers`)
        .then(res => res.json())
        .then(data => {
          setRecentFollowers(data.followers);
        })
        .catch(err => console.error('Error loading followers:', err));

      // Fetch notifications (initial load handled by polling effect below)
    }
  }, [isAuthenticated, user]);

  // Poll for notifications every 30 seconds
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const fetchNotifications = () => {
      fetch(`${SERVER_URL}/users/${user.id}/notifications`)
        .then(res => res.json())
        .then(data => setNotifications(data.notifications || []))
        .catch(err => console.error('Error loading notifications:', err));
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, NOTIFICATION_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [isAuthenticated, user]);

  // Helper function to mark notification as read
  const markNotificationAsRead = notificationId => {
    // Only mark if not already read to avoid unnecessary updates
    setNotifications(prev => {
      const notification = prev.find(n => n.id === notificationId);
      if (!notification || notification.is_read) {
        return prev; // Already read or doesn't exist
      }

      // Mark as read immediately in state for instant UI update
      return prev.map(n => (n.id === notificationId ? { ...n, is_read: true } : n));
    });

    // Then update on backend
    fetch(`${SERVER_URL}/notifications/${notificationId}/read`, {
      method: 'PUT',
    }).catch(err => {
      console.error('Error marking notification as read:', err);
      // Revert state on error
      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, is_read: false } : n))
      );
    });
  };

  return (
    <nav className="bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-2 sm:py-4">
        <div className="flex justify-between items-center gap-2">
          <div className="flex-shrink-0">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xl sm:text-2xl font-display font-bold text-indigo-600 hover:text-indigo-700 transition-all tracking-tight"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 sm:w-7 sm:h-7 flex-shrink-0">
                <path d="M9.375 3a1.875 1.875 0 0 0 0 3.75h1.875v4.5H3.375A1.875 1.875 0 0 1 1.5 9.375v-.75c0-1.036.84-1.875 1.875-1.875h3.193A3.375 3.375 0 0 1 12 2.753a3.375 3.375 0 0 1 5.432 3.997h3.193c1.035 0 1.875.84 1.875 1.875v.75c0 1.036-.84 1.875-1.875 1.875H13.5v-4.5h1.875a1.875 1.875 0 1 0-1.875-1.875V6.75h-3V4.875C10.5 3.839 9.911 3 9.375 3ZM11.25 12.75H3v6.75a2.25 2.25 0 0 0 2.25 2.25h6v-9ZM12.75 12.75v9h6.75a2.25 2.25 0 0 0 2.25-2.25v-6.75h-9Z" />
              </svg>
              Gifted
            </Link>
          </div>
          <div className="flex gap-1 sm:gap-2 items-center flex-wrap justify-end">
            <Link
              to="/about"
              className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
            >
              About
            </Link>
            <Link
              to="/faq"
              className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
            >
              FAQ
            </Link>

            {user?.is_admin && (
              <Link
                to="/admin"
                className="hidden sm:block px-2 sm:px-3 py-1.5 text-xs sm:text-sm text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors"
              >
                Admin
              </Link>
            )}
            {isAuthenticated ? (
              <>
                {/* Notifications Bell */}
                <div className="relative" ref={notificationsRef}>
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="relative px-2 sm:px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.437L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {(() => {
                      // Only count unread notifications (followers are no longer shown in dropdown)
                      const unreadNotificationsCount = notifications.filter(n => !n.is_read).length;
                      const totalNotifications = unreadNotificationsCount;

                      return (
                        totalNotifications > 0 && (
                          <span className="absolute top-0 right-0 inline-flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 text-[10px] sm:text-xs font-bold text-white bg-red-500 rounded-full">
                            {totalNotifications}
                          </span>
                        )
                      );
                    })()}
                  </button>

                  {/* Notifications Dropdown */}
                  {showNotifications && (
                    <div className="absolute left-1/2 -translate-x-1/2 sm:left-auto sm:right-0 sm:translate-x-0 mt-2 w-[calc(100vw-1rem)] sm:w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[80vh] overflow-hidden flex flex-col">
                      <div className="p-4 border-b border-gray-200">
                        <h3 className="font-semibold text-gray-900">Notifications</h3>
                        <p className="text-xs text-gray-500 mt-1">Gift pools & followers</p>
                      </div>
                      <div className="overflow-y-auto flex-1">
                        {/* Notifications Section */}
                        {notifications.length > 0 && (
                          <div className="border-b border-gray-200">
                            <div className="px-4 py-2 bg-gray-50">
                              <h4 className="text-xs font-semibold text-gray-700 uppercase">
                                Notifications
                              </h4>
                            </div>
                            {notifications
                              .slice(0, showAllNotifications ? undefined : 10)
                              .map(notif => {
                                const getNotificationIcon = type => {
                                  switch (type) {
                                    case 'new_like':
                                      return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ef4444" className="w-4 h-4 mt-0.5 flex-shrink-0"><path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001Z" /></svg>;
                                    case 'new_vouch':
                                      return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#d97706" className="w-4 h-4 mt-0.5 flex-shrink-0"><path fillRule="evenodd" d="M12.516 2.17a.75.75 0 0 0-1.032 0 11.209 11.209 0 0 1-7.877 3.08.75.75 0 0 0-.722.515A12.74 12.74 0 0 0 2.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 0 0 .374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 0 0-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08Zm3.094 8.016a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" /></svg>;
                                    case 'new_comment':
                                      return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#d97706" className="w-4 h-4 mt-0.5 flex-shrink-0"><path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0 1 12 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 0 1-3.476.383.39.39 0 0 0-.297.17l-2.755 4.133a.75.75 0 0 1-1.248 0l-2.755-4.133a.39.39 0 0 0-.297-.17 48.9 48.9 0 0 1-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97Z" clipRule="evenodd" /></svg>;
                                    case 'comment_liked':
                                      return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#ef4444" className="w-4 h-4 mt-0.5 flex-shrink-0"><path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001Z" /></svg>;
                                    default:
                                      return null;
                                  }
                                };

                                const getNotificationStyle = type => {
                                  switch (type) {
                                    case 'goal_reached':
                                    case 'pool_accepted':
                                      return 'bg-green-50 border-l-4 border-green-500';
                                    case 'pool_declined':
                                      return 'bg-red-50 border-l-4 border-red-400';
                                    default:
                                      return 'bg-indigo-50 border-l-4 border-indigo-400';
                                  }
                                };

                                const getTimeAgo = dateString => {
                                  // Ensure the date is parsed as UTC by adding 'Z' if not present
                                  const utcDateString = dateString.endsWith('Z')
                                    ? dateString
                                    : dateString + 'Z';
                                  const date = new Date(utcDateString);
                                  const now = Date.now();
                                  const seconds = Math.max(0, Math.floor((now - date) / 1000));

                                  if (seconds < 60) return 'just now';
                                  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
                                  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
                                  if (seconds < 604800)
                                    return `${Math.floor(seconds / 86400)}d ago`;
                                  return date.toLocaleDateString();
                                };

                                return (
                                  <div
                                    key={notif.id}
                                    className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${getNotificationStyle(notif.notification_type)} ${!notif.is_read ? 'font-semibold' : ''}`}
                                    onClick={() => {
                                      // Mark notification as read
                                      markNotificationAsRead(notif.id);

                                      // Handle pool_created notifications differently - extract acceptance URL from message
                                      if (notif.notification_type === 'pool_created') {
                                        // Extract the acceptance URL from the message
                                        const urlMatch = notif.message.match(/(https?:\/\/[^\s]+)/);
                                        if (urlMatch) {
                                          const acceptanceUrl = urlMatch[0];
                                          // Navigate to the acceptance page
                                          window.location.href = acceptanceUrl;
                                          setShowNotifications(false);
                                        }
                                      } else if (notif.post?.id && notif.post?.user_id) {
                                        // Navigate to related post for other notification types
                                        navigate(
                                          `/profile/${notif.post.user_id}#post-${notif.post.id}`
                                        );
                                        setShowNotifications(false);
                                      }
                                    }}
                                  >
                                    <div className="flex items-start gap-2">
                                      {getNotificationIcon(notif.notification_type)}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-900 mb-1">{notif.title}</p>
                                        <p className="text-xs text-gray-600 break-words">
                                          {(() => {
                                            // Parse message for URLs and make them clickable
                                            const urlRegex = /(https?:\/\/[^\s]+)/g;
                                            const parts = notif.message.split(urlRegex);

                                            // Determine link text based on notification type
                                            const getLinkText = type => {
                                              switch (type) {
                                                case 'pool_created':
                                                  return 'Review and accept';
                                                case 'new_gift':
                                                  return 'Click here to claim';
                                                case 'payout_available':
                                                  return 'Request payout';
                                                default:
                                                  return 'Click here';
                                              }
                                            };

                                            return parts.map((part, index) => {
                                              if (part.match(urlRegex)) {
                                                return (
                                                  <a
                                                    key={index}
                                                    href={part}
                                                    onClick={e => {
                                                      e.stopPropagation();
                                                      // Mark notification as read
                                                      markNotificationAsRead(notif.id);
                                                      setShowNotifications(false);
                                                    }}
                                                    className="text-indigo-600 hover:text-indigo-800 underline font-semibold"
                                                  >
                                                    {getLinkText(notif.notification_type)}
                                                  </a>
                                                );
                                              }
                                              return part;
                                            });
                                          })()}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-1">
                                          {getTimeAgo(notif.created_at)}
                                        </p>
                                      </div>
                                      {!notif.is_read && (
                                        <div className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-1"></div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            {notifications.length > 10 && (
                              <button
                                onClick={() => setShowAllNotifications(!showAllNotifications)}
                                className="w-full px-4 py-2 text-xs text-indigo-600 hover:bg-indigo-50 transition-colors"
                              >
                                {showAllNotifications
                                  ? 'Show Less'
                                  : `Show All (${notifications.length})`}
                              </button>
                            )}
                          </div>
                        )}

                        {/* Empty State */}
                        {pendingPools.length === 0 &&
                          acceptedPools.length === 0 &&
                          recentFollowers.length === 0 &&
                          notifications.length === 0 && (
                            <div className="p-8 text-center">
                              <p className="text-sm text-gray-500">No notifications</p>
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Profile dropdown */}
                <div className="relative" ref={profileMenuRef}>
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Avatar name={user.username} email={user.email} profilePhotoUrl={user.profile_photo_url} size="w-7 h-7" textSize="text-xs" />
                    <span className="hidden sm:inline text-sm font-medium truncate max-w-[100px]">{user.username}</span>
                    <svg className="w-3.5 h-3.5 hidden sm:block text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden py-1">
                      <Link
                        to={`/profile/${user.id}`}
                        onClick={() => { setShowProfileMenu(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Profile
                      </Link>
                      {user?.is_admin && (
                        <Link
                          to="/admin"
                          onClick={() => setShowProfileMenu(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          Admin
                        </Link>
                      )}
                      <Link
                        to="/settings"
                        onClick={() => setShowProfileMenu(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Settings
                      </Link>
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          onClick={() => { setShowProfileMenu(false); logout(); }}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                        >
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 hover:text-gray-900 font-medium transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="px-3 sm:px-5 py-1.5 sm:py-2.5 bg-indigo-500 text-white rounded-xl font-semibold shadow-md hover:shadow-lg hover:bg-indigo-600 transition-all text-xs sm:text-sm"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

    </nav>
  );
}

export default Navigation;
